import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { getAuth, signOut } from '../lib/auth.js'
import { redirectToOAuthSignIn } from '../utils/oauth.js'
import { useAuthConfig } from '../config/appConfig.jsx'
import { onSessionExpired } from '../hooks/useTokenRefresh.js'
import ErrorAlert from './ErrorAlert.jsx'
import { BTN_PRIMARY } from '../lib/ui.js'

// Shown when proactive refresh has failed twice (or the AS rejected the
// refresh grant with 400/401, meaning the refresh token itself is dead).
//
// Re-auth is OAuth-only. No password form here — the only path is the same
// /oauth2/authorize flow used at first sign-in. The AS may complete it
// silently if the bitol_session cookie is still valid.
//
// Adapted from Workbench origin/main@60e779e:src/components/ui/SessionExpiredModal.jsx.
export default function SessionExpiredModal() {
  const authConfig = useAuthConfig()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const off = onSessionExpired(() => setOpen(true))
    return () => off()
  }, [])

  if (!open) return null

  const auth = getAuth()

  const handleSignIn = async () => {
    setLoading(true)
    setError(null)
    try {
      setOpen(false)
      await redirectToOAuthSignIn(authConfig, {
        returnTo: window.location.pathname + window.location.search,
      })
    } catch (e) {
      setError({ title: 'Sign-in unavailable', message: e.message })
      setOpen(true)
      setLoading(false)
    }
  }

  const handleLogout = () => {
    setOpen(false)
    signOut({ issuer: authConfig?.issuer })
  }

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative w-full max-w-md rounded-xl bg-white p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <h2 className="text-lg font-semibold text-slate-900">Session expired</h2>
          <p className="mt-1 text-sm text-slate-600">
            {auth?.email ? (
              <>Your session for <strong>{auth.email}</strong> has expired. Sign in again to continue.</>
            ) : (
              'Your session has expired. Sign in again to continue.'
            )}
          </p>
        </div>

        {error ? (
          <div className="mb-4">
            <ErrorAlert title={error.title} message={error.message} source="SessionExpiredModal" />
          </div>
        ) : null}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleSignIn}
            disabled={loading}
            className={`${BTN_PRIMARY} flex-1`}
          >
            {loading ? 'Redirecting…' : 'Sign in with Bitol'}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            disabled={loading}
            className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
          >
            Log out
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
